import { Button } from "@/components/ui/button";
import { Media } from "@/components/Media";
import { DEMO_URL, PLATFORM_URL } from "@/lib/navigation";
import { trackDemoClick } from "@/lib/telemetry";
import cartVisual from "@/assets/solution-cart-v2.webp";
import uiVisual from "@/assets/tech-ui-v2.webp";

/**
 * Homepage hero — the definitive introduction to the PathFree
 * intelligent-resuscitation ecosystem (datasyteAI™ + AiMediQ™).
 */
export const HomeHero = () => (
  <section className="band-dark relative overflow-hidden border-b border-border">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 opacity-50"
      style={{
        backgroundImage:
          "linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)",
        backgroundSize: "88px 88px",
        maskImage: "radial-gradient(120% 90% at 18% 0%, black 25%, transparent 75%)",
        WebkitMaskImage: "radial-gradient(120% 90% at 18% 0%, black 25%, transparent 75%)",
      }}
    />

    <div className="ds-container relative py-20 lg:py-28">
      <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-16">
        <div className="reveal space-y-7">
          <p className="eyebrow eyebrow-dot">datasyteAI™ + AiMediQ™</p>
          <h1 className="ds-display">
            Intelligence for the{" "}
            <span className="text-accent">moments that cannot wait.</span>
          </h1>
          <p className="ds-lead max-w-xl">
            An integrated resuscitation platform designed to bring real-time decision
            support, coordinated workflows, intelligent documentation, and post-event
            insight into one connected system.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Button asChild size="lg">
              <a href={DEMO_URL} onClick={() => trackDemoClick("home-hero")}>
                Request a Demo
              </a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href={PLATFORM_URL}>Explore the Platform</a>
            </Button>
          </div>
          <div className="hairline max-w-xl pt-5">
            <p className="text-sm font-semibold text-foreground">Regulatory status</p>
            <p className="ds-caption">
              AiMediQ™ powered by datasyteAI™ is currently in the pre-market /
              development &amp; validation phase. Not FDA cleared or approved; not for
              clinical use or sale.
            </p>
          </div>
        </div>

        <div className="reveal">
          <div className="relative">
            <figure className="media-frame relative overflow-hidden">
              <Media
                src={cartVisual}
                alt="Concept visualization of the AiMediQ™ connected resuscitation cart at the point of care"
                className="h-auto w-full object-cover"
                sizes="(max-width: 1024px) 100vw, 660px"
                eager
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/95 to-transparent px-4 pb-3 pt-12 text-right">
                <figcaption className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground">
                  Concept visualization
                </figcaption>
              </div>
            </figure>

            <figure className="media-frame absolute bottom-5 left-5 hidden w-48 overflow-hidden shadow-xl sm:block lg:-left-10 lg:w-60">
              <Media
                src={uiVisual}
                alt="Concept visualization of the datasyteAI™ interface showing a shared event timeline"
                className="h-auto w-full object-cover"
                sizes="256px"
              />
              <figcaption className="bg-card px-3 py-2 font-mono text-[0.625rem] uppercase tracking-[0.2em] text-muted-foreground">
                datasyteAI™ interface · Concept
              </figcaption>
            </figure>
          </div>

          <p className="ds-legal mt-5">
            Concept visuals shown for illustration during the pre-market phase. Not a
            commercially deployed product.
          </p>
        </div>


      </div>
    </div>
  </section>
);
